import axios from 'axios'

const ENDPOINTS = {
    GET_COMPANY_INTEGRATIONS: companyId =>
        `companies/${companyId}/integrations`,

    GET_SLACK_STATUS: companyId => `companies/${companyId}/integrations/slack`,
    DISCONNECTED_SLACK: companyId =>
        `companies/${companyId}/integrations/slack/disconnect`,
    FINISH_SLACK: companyId =>
        `companies/${companyId}/integrations/slack/oauth`,
    GET_SLACK_OPTIONS: companyId =>
        `companies/${companyId}/integrations/slack/options`,
    SAVE_SLACK_OPTIONS: companyId =>
        `companies/${companyId}/integrations/slack/options`,

    GET_DROPBOX_STATUS: companyId =>
        `companies/${companyId}/integrations/dropbox`,
    DISCONNECTED_DROPBOX: companyId =>
        `companies/${companyId}/integrations/dropbox/disconnect`,
    FINISH_DROPBOX: companyId =>
        `companies/${companyId}/integrations/dropbox/oauth`,

    GET_SHAREPOINT_STATUS: companyId =>
        `companies/${companyId}/integrations/sharepoint`,
    DISCONNECTED_SHAREPOINT: companyId =>
        `companies/${companyId}/integrations/sharepoint/disconnect`,
    FINISH_SHAREPOINT: companyId =>
        `companies/${companyId}/integrations/sharepoint/oauth`,
    SAVE_SHAREPOINT_OPTIONS: companyId =>
        `companies/${companyId}/integrations/sharepoint/options`,
    GET_SHAREPOINT_AUTH_URL: companyId =>
        `companies/${companyId}/integrations/sharepoint/auth-url`
}

class Service {
    /**
     * Get company integrations
     *
     * @param {String|Number} companyId
     *
     * @returns Promise
     */
    async getCompanyIntegrations (companyId) {
        try {
            const route = ENDPOINTS.GET_COMPANY_INTEGRATIONS(companyId)
            const { data } = await axios.get(route)

            return data
        } catch (error) {
            throw error
        }
    }

    /**
     * Get slack status
     *
     * @param {String|Number} companyId
     *
     * @returns Promise
     */
    async getSlackStatus (companyId) {
        try {
            const route = ENDPOINTS.GET_SLACK_STATUS(companyId)
            const { data } = await axios.get(route)

            return data
        } catch (error) {
            throw error
        }
    }

    /**
     * Finish oauth slack
     *
     * @param {String|Number} companyId
     * @param {Object} payload
     *
     * @returns Promise
     */
    async finishOauthSlack (companyId, payload) {
        try {
            const route = ENDPOINTS.FINISH_SLACK(companyId)
            const { data } = await axios.post(route, payload)

            return data
        } catch (error) {
            throw error
        }
    }

    /**
     * Get slack options
     *
     * @param {String|Number} companyId
     *
     * @returns Promise
     */
    async getSlackOptions (companyId) {
        try {
            const route = ENDPOINTS.GET_SLACK_OPTIONS(companyId)
            const { data } = await axios.get(route)

            return data
        } catch (error) {
            throw error
        }
    }

    /**
     * Save slack options
     *
     * @param {String|Number} companyId
     * @param {Object} payload
     *
     * @returns Promise
     */
    async saveSlackOptions (companyId, payload) {
        try {
            const route = ENDPOINTS.SAVE_SLACK_OPTIONS(companyId)
            const { data } = await axios.post(route, payload)

            return data
        } catch (error) {
            throw error
        }
    }

    /**
     * Disconnect slack
     *
     * @param {String|Number} companyId
     *
     * @returns Promise
     */
    async disconnectSlack (companyId) {
        try {
            const route = ENDPOINTS.DISCONNECTED_SLACK(companyId)
            const { data } = await axios.post(route)

            return data
        } catch (error) {
            throw error
        }
    }

    /**
     * Get dropbox status
     *
     * @param {String|Number} companyId
     *
     * @returns Promise
     */
    async getDropboxStatus (companyId) {
        try {
            const route = ENDPOINTS.GET_DROPBOX_STATUS(companyId)
            const { data } = await axios.get(route)

            return data
        } catch (error) {
            throw error
        }
    }

    /**
     * Finish oauth dropbox
     *
     * @param {String|Number} companyId
     * @param {Object} payload
     *
     * @returns Promise
     */
    async finishOauthDropbox (companyId, payload) {
        try {
            const route = ENDPOINTS.FINISH_DROPBOX(companyId)
            const { data } = await axios.post(route, payload)

            return data
        } catch (error) {
            throw error
        }
    }

    /**
     * Disconnect dropbox
     *
     * @param {String|Number} companyId
     *
     * @returns Promise
     */
    async disconnectDropbox (companyId) {
        try {
            const route = ENDPOINTS.DISCONNECTED_DROPBOX(companyId)
            const { data } = await axios.post(route)

            return data
        } catch (error) {
            throw error
        }
    }

    /**
     * Get sharepoint status
     *
     * @param {String|Number} companyId
     *
     * @returns Promise
     */
    async getSharepointStatus (companyId) {
        try {
            const route = ENDPOINTS.GET_SHAREPOINT_STATUS(companyId)
            const { data } = await axios.get(route)

            return data
        } catch (error) {
            throw error
        }
    }

    /**
     * Finish oauth sharepoint
     *
     * @param {String|Number} companyId
     * @param {Object} payload
     *
     * @returns Promise
     */
    async finishOauthSharepoint (companyId, payload) {
        try {
            const route = ENDPOINTS.FINISH_SHAREPOINT(companyId)
            const { data } = await axios.post(route, payload)

            return data
        } catch (error) {
            throw error
        }
    }

    /**
     * Save sharepoint options
     *
     * @param {String|Number} companyId
     * @param {Object} payload
     *
     * @returns Promise
     */
    async saveSharepointOptions (companyId, payload) {
        try {
            const route = ENDPOINTS.SAVE_SHAREPOINT_OPTIONS(companyId)
            const { data } = await axios.post(route, payload)

            return data
        } catch (error) {
            throw error
        }
    }

    /**
     * Disconnect sharepoint
     *
     * @param {String|Number} companyId
     *
     * @returns Promise
     */
    async disconnectSharepoint (companyId) {
        try {
            const route = ENDPOINTS.DISCONNECTED_SHAREPOINT(companyId)
            const { data } = await axios.post(route)

            return data
        } catch (error) {
            throw error
        }
    }

    /**
     * Get SharePoint OAuth auth-flow URL with the user's site URL carried
     * via the signed `state` parameter. Used by Phase 3 of the wizard
     * before redirecting the user to the Microsoft consent dialog so the
     * callback handler can resolve + probe the site URL atomically.
     *
     * @param {String|Number} companyId
     * @param {Object} payload — `{ url: <user-entered SharePoint site URL> }`
     *
     * @returns Promise<{ auth_flow_url: string }>
     */
    async getSharepointAuthUrl (companyId, payload) {
        try {
            const route = ENDPOINTS.GET_SHAREPOINT_AUTH_URL(companyId)
            const { data } = await axios.post(route, payload)

            return data
        } catch (error) {
            throw error
        }
    }
}

export const service = new Service()
